import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { ArrowLeft, UsersRound, Loader2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import axios from 'axios';
import { API_URL as API } from '@/utils/apiConfig';

import { useSafeBack } from '@/utils/useSafeBack';
const LAST_UPDATED = 'March 29, 2026';

export default function CommunityGuidelinesPage() {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const goBackSafe = useSafeBack('/home');
  const [dynamicContent, setDynamicContent] = useState(null);
  const [dynamicDate, setDynamicDate] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    async function fetchGuidelines() {
      try {
        const lang = (i18n.language || 'en').split('-')[0];
        const res = await axios.get(`${API}/legal/community-guidelines`, { params: { locale: lang } });
        const servedLocale = res.data.content_locale || 'en';
        if (res.data.content && servedLocale === lang) {
          setDynamicContent(res.data.content);
          if (res.data.updated_at) setDynamicDate(new Date(res.data.updated_at).toLocaleDateString(lang === 'en' ? 'en-US' : lang, { year: 'numeric', month: 'long', day: 'numeric' }));
        }
      } catch { /* fall back to hardcoded */ }
      finally { setLoading(false); }
    }
    fetchGuidelines();
  }, [i18n.language]);

  return (
    <div className="page-container" style={{ background: 'var(--theme-background, #0f172a)', color: 'var(--theme-text, #f1f5f9)' }}>
      <header className="sticky top-0 z-50 px-4 py-4 shadow-sm backdrop-blur-xl" style={{ background: 'var(--theme-surface, #1e293b)', borderBottom: '1px solid var(--glass-border, #334155)' }}>
        <div className="max-w-3xl mx-auto flex items-center gap-3">
          <Button variant="ghost" size="sm" onClick={goBackSafe} className="rounded-full h-9 w-9 p-0" data-testid="guidelines-back-btn">
            <ArrowLeft className="h-4 w-4" />
          </Button>
          <UsersRound className="h-5 w-5" style={{ color: 'var(--theme-accent, #6366f1)' }} />
          <h1 className="text-base font-semibold">{t('guidelines.title')}</h1>
        </div>
      </header>
      <main className="page-content max-w-3xl mx-auto px-4 py-8 pb-20">
        {loading ? (
          <div className="flex justify-center py-12"><Loader2 className="h-6 w-6 animate-spin" style={{ color: 'var(--theme-accent)' }} /></div>
        ) : dynamicContent ? (
          <div className="space-y-6" style={{ lineHeight: 1.75 }}>
            <p className="text-xs uppercase tracking-wider" style={{ color: 'var(--theme-textSecondary, #94a3b8)' }}>
              {t('guidelines.last_updated')}: {dynamicDate || LAST_UPDATED}
            </p>
            <div className="prose prose-sm prose-invert max-w-none" style={{ color: 'var(--theme-text, #f1f5f9)' }} dangerouslySetInnerHTML={{ __html: dynamicContent }} />
          </div>
        ) : (
        <div className="space-y-8" style={{ lineHeight: 1.75 }}>
          <div>
            <p className="text-xs uppercase tracking-wider mb-2" style={{ color: 'var(--theme-textSecondary, #94a3b8)' }}>
              {t('guidelines.last_updated')}: {LAST_UPDATED}
            </p>
            <p className="text-sm" style={{ color: 'var(--theme-textSecondary, #94a3b8)' }}>{t('guidelines.intro')}</p>
          </div>

          {/* 1. Purpose */}
          <Section title={t('guidelines.s1_title')}>
            <p>{t('guidelines.s1_body')}</p>
          </Section>

          {/* 2. Be Respectful */}
          <Section title={t('guidelines.s2_title')}>
            <p>{t('guidelines.s2_intro')}</p>
            <ul className="list-disc pl-5 space-y-2 mt-3">
              {t('guidelines.s2_items', { returnObjects: true }).map((item, i) => <li key={i}>{item}</li>)}
            </ul>
          </Section>

          {/* 3. Prohibited Content */}
          <Section title={t('guidelines.s3_title')}>
            <p>{t('guidelines.s3_intro')}</p>
            <ul className="list-disc pl-5 space-y-2 mt-3">
              {t('guidelines.s3_items', { returnObjects: true }).map((item, i) => (
                <li key={i} dangerouslySetInnerHTML={{ __html: item }} />
              ))}
            </ul>
          </Section>

          {/* 4. Scripture Sharing */}
          <Section title={t('guidelines.s4_title')}>
            <p>{t('guidelines.s4_intro')}</p>
            <ul className="list-disc pl-5 space-y-2 mt-3">
              {t('guidelines.s4_items', { returnObjects: true }).map((item, i) => <li key={i}>{item}</li>)}
            </ul>
          </Section>

          {/* 5. Reporting */}
          <Section title={t('guidelines.s5_title')}>
            <p>{t('guidelines.s5_intro')}</p>
            <ul className="list-disc pl-5 space-y-2 mt-3">
              {t('guidelines.s5_items', { returnObjects: true }).map((item, i) => (
                <li key={i} dangerouslySetInnerHTML={{ __html: item }} />
              ))}
            </ul>
          </Section>

          {/* 6. Consequences */}
          <Section title={t('guidelines.s6_title')}>
            <p>{t('guidelines.s6_intro')}</p>
            <ul className="list-disc pl-5 space-y-2 mt-3">
              {t('guidelines.s6_items', { returnObjects: true }).map((item, i) => (
                <li key={i} dangerouslySetInnerHTML={{ __html: item }} />
              ))}
            </ul>
          </Section>

          {/* 7. Age */}
          <Section title={t('guidelines.s7_title')}>
            <p>{t('guidelines.s7_body')}</p>
          </Section>

          {/* 8. Privacy in Community */}
          <Section title={t('guidelines.s8_title')}>
            <ul className="list-disc pl-5 space-y-2">
              {t('guidelines.s8_items', { returnObjects: true }).map((item, i) => <li key={i}>{item}</li>)}
            </ul>
          </Section>

          {/* 9. Changes */}
          <Section title={t('guidelines.s9_title')}>
            <p>{t('guidelines.s9_body')}</p>
          </Section>

          {/* 10. Contact */}
          <Section title={t('guidelines.s10_title')}>
            <p>
              {t('guidelines.s10_body')}{' '}
              <a href="mailto:support@askeili.com" className="underline font-medium" style={{ color: 'var(--theme-accent, #6366f1)' }}>
                {t('generated.communityguidelinespage_support_askeili_com_31b3')}
              </a>.
            </p>
          </Section>
        </div>
        )}
      </main>
    </div>
  );
}

function Section({ title, children }) {
  return (
    <section>
      <h2 className="text-base font-semibold mb-3" style={{ color: 'var(--theme-text, #f1f5f9)' }}>{title}</h2>
      <div className="text-sm space-y-2" style={{ color: 'var(--theme-textSecondary, #cbd5e1)' }}>{children}</div>
    </section>
  );
}
